// React and third-party libraries
import * as React from "react";
import styled from "styled-components";
import { motion } from 'framer-motion';
import { navigate } from "gatsby";
import { useTranslation } from "react-i18next";

// Components imports
import Button from "./ui/Button";

// Local imports (styles, constants, etc.)
import { colors, media } from "../style/constants";


/* Styled Components */

// Section wrapper with optional background color and padding
const SectionWrapper = styled.section`
  position: relative;
  overflow: hidden;
  padding-top: 2rem;
  padding-bottom: 2rem;
  background-color: ${({ $background }) => $background || "inherit"};

  @media (min-width: ${media.desktopSm}px) {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }
`;

// Container to center content and define max-width
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
`;

// Text content aligned to center
const TextContent = styled(motion.div)`
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
`;

// Heading style for call-to-action
const Heading = styled(motion.h1)`
  font-size: 1.755rem;
  line-height: 2.25rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 0.825rem;
  
  @media (min-width: ${media.tabletSm}px) {
    margin-bottom: 1.5rem;
    font-size: 2.25rem;
    line-height: 2.5rem;
  }
`;

// Supporting paragraph text with dynamic color
const Paragraph = styled.p`
  font-size: 1.125rem;
  line-height: 1.75rem;
  margin-bottom: 1.25rem;
  color: ${({ $color }) => $color || colors.lightGrey};

  @media (min-width: ${media.tabletSm}px) {
    margin-bottom: 1.825rem;
    font-size: 1.125rem;
    line-height: 1.75rem;
  }
`;


/**
 * GetInTouchSection Component - CTA section with dynamic styling based on layout
 * 
 * @param {boolean} secondary - If true, applies dark background and white text
 */
const GetInTouchSection = ({ secondary }) => {
  const { t } = useTranslation();

  return (
    <SectionWrapper $background={secondary ? colors.darkBg : "inherit"}>
      <Container>
        <TextContent>
          <Heading>{t("getInTouchSection.heading")}</Heading>
          <Paragraph $color={secondary ? colors.white : colors.lightGrey}>
            {t("getInTouchSection.description")}
          </Paragraph>
          <Button 
            size="small" 
            type="white"
            onClick={() => navigate(`/contact`)}  
          >
            {t("getInTouchSection.buttonText")}
          </Button>
        </TextContent>
      </Container>
    </SectionWrapper>
  );
};

export default GetInTouchSection;
